import React, { useEffect, useState } from 'react';
import { fetchSystemConfig } from '../services/firebaseService';
import Header from './layout/Header';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../services/localeHelpers';

const PrivacyPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPolicy = async () => {
      try {
        const config = await fetchSystemConfig('privacy_policy');
        if (config) {
          const lang = i18n.language === 'no' ? 'no' : 'en';
          setContent(config[lang] || config['no'] || 'Privacy policy is being updated...');
        } else {
          setContent('Personvernserklæring er under oppdatering...');
        }
      } catch (error) {
        console.error('Failed to load privacy policy:', error);
        setContent('Kunne ikke laste personvernserklæringen.');
      } finally {
        setIsLoading(false);
      }
    };
    loadPolicy();
  }, [i18n.language]);

  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    document.title = 'Personvernserklæring – Fricamp';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Personvernserklæring for Fricamp-applikasjonen. Les hvordan vi behandler dine data.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://app.fricamp.com${window.location.pathname}`);

    return () => {
      document.title = 'Fricamp';
      metaDesc?.remove();
      canonical?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        showSearch={false}
        searchQuery=""
        setSearchQuery={() => { }}
        searchResults={[]}
        onResultClick={() => { }}
        onCategoryClick={() => { }}
        currentUser={null}
        onAuthClick={() => { window.location.href = '/?login=1'; }}
        onLogoClick={() => { window.location.href = '/'; }}
        onShowProfile={() => { }}
        onLogout={() => { }}
        filterUnknownSurface={false}
        onToggleSurfaceFilter={() => { }}
        filterVerified={false}
        onToggleVerifiedFilter={() => { }}
        vehicleType="standard"
        onSetVehicleType={() => { }}
        isDev={false}
      />

      <main className="max-w-4xl mx-auto px-4 py-12 md:py-20">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 px-6 py-8 md:px-10 md:py-12 text-white">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="text-emerald-400" size={32} />
              <h1 className="text-3xl md:text-4xl font-bold">{t("auto.privacypolicy.personvernserklaering", "Personvernserklæring")}</h1>
            </div>
            <p className="text-slate-300 text-lg max-w-2xl">
              {t("auto.privacypolicy.informasjon_om_hvordan_takle_c", "Informasjon om hvordan Takle Consulting håndterer dine personopplysninger i Fricamp-applikasjonen.")}
            </p>
          </div>

          <div className="px-6 py-10 md:px-12 md:py-16">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Loader2 className="animate-spin text-slate-400" size={48} />
                <p className="text-slate-500 font-medium">{t("auto.privacypolicy.laster_innhold", "Laster innhold...")}</p>
              </div>
            ) : (
              <div className="prose prose-slate max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-p:text-slate-600 prose-p:leading-relaxed whitespace-pre-wrap">
                {content}
              </div>
            )}

            <div className="mt-16 pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-sm text-slate-400">
              <p>Sist oppdatert: {new Date().toLocaleDateString(getDateLocale())}</p>
              <p>© {new Date().getFullYear()} Takle Consulting</p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <a
            href="/"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-200"
          >
            {t("auto.privacypolicy.tilbake_til_kartet", "Tilbake til kartet")}
          </a>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
